import { useEffect, useRef } from 'react'

import { DEFAULT_THUMBNAIL_NAME, buildImagePath } from 'src/utils'

import { useCreateModuleStyles } from './DisplayRichText.styles'
interface Props {
	content: string
}

const iframeYoutubeStyles: Partial<CSSStyleDeclaration> = {
	border: 'none',
	overflow: 'hidden',
	width: '100%',
	height: '100%',
	position: 'absolute',
	borderRadius: '10px'
}
const iframeYoutubeFigureStyles: Partial<CSSStyleDeclaration> = {
	height: '0',
	position: 'relative',
	paddingBottom: '56.25%'
}

const defaultImageStyles: Partial<CSSStyleDeclaration> = {
	width: '100%',
	cursor: 'pointer',
	borderRadius: '10px'
}

const customVideoFigureStyles: Partial<CSSStyleDeclaration> = {
	display: 'flex'
}

const customVideoStyles: Partial<CSSStyleDeclaration> = {
	borderRadius: '10px',
	width: '100%'
}

export const DisplayRichText = ({ content }: Props) => {
	const videoRef = useRef<HTMLDivElement>(null)

	const classes = useCreateModuleStyles()

	const applyStyles = (element: HTMLElement, styles: Partial<CSSStyleDeclaration>) => {
		for (const [key, value] of Object.entries(styles)) {
			element.style[key as 'width'] = value as string
		}
	}

	useEffect(() => {
		if (!videoRef.current) return
		videoRef.current.innerHTML = ''
		videoRef.current.insertAdjacentHTML('beforeend', content)
		const videos = videoRef.current.getElementsByTagName('figure')

		if (videos?.length) {
			for (const video of videos) {
				// images also arrives with figure tag
				if (video.innerHTML.includes('<img')) continue

				// if in some part of the video there is a youtube video, don't replace it

				if (video.innerHTML.includes('youtube.com') || video.innerHTML.includes('vimeo.com')) {
					// ckeditor render youtube videos as oembed or iframe elements
					let targetElement: Element | undefined
					targetElement = video.getElementsByTagName('oembed')[0]

					if (!targetElement) {
						targetElement = video.getElementsByTagName('iframe')[0]
					}

					if (!targetElement) continue

					const iframe = document.createElement('iframe')

					iframe.src = targetElement.getAttribute('url') || targetElement.getAttribute('src') || ''
					applyStyles(iframe, iframeYoutubeStyles)

					const videoFigure = document.createElement('figure')
					applyStyles(videoFigure, iframeYoutubeFigureStyles)
					videoFigure.appendChild(iframe)

					replaceNode(videoRef.current, { target: videoFigure, current: video })
					continue
				}

				const defaultImage = document.createElement('img')
				defaultImage.src = buildImagePath(DEFAULT_THUMBNAIL_NAME)
				applyStyles(defaultImage, defaultImageStyles)
				// figure element will allow us to apply some default styles to the video
				const newFigure = document.createElement('figure')
				applyStyles(newFigure, customVideoFigureStyles)

				newFigure.appendChild(defaultImage)

				defaultImage.addEventListener(
					'click',
					() => {
						const iframe = video.getElementsByTagName('iframe')[0] as HTMLIFrameElement
						const videoTag = video.getElementsByTagName('source')[0] as HTMLSourceElement
						const src: string = iframe?.src || videoTag?.src || ''
						const sourceTag = document.createElement('source')

						const newVideo = document.createElement('video')

						sourceTag.src = src
						newVideo.appendChild(sourceTag)
						newVideo.controls = true
						applyStyles(newVideo, customVideoStyles)

						newVideo.addEventListener('loadeddata', () => {
							newVideo.play()
						})

						replaceNode(newFigure as HTMLDivElement, {
							target: newVideo,
							current: defaultImage
						})
					},
					false
				)

				replaceNode(videoRef.current, { target: newFigure, current: video })
			}
		}
	}, [content])

	const replaceNode = (
		parent: HTMLDivElement,
		{ target, current }: { target: HTMLElement; current: HTMLElement }
	) => {
		parent.replaceChild(target, current)
	}

	return <div className={classes.content} ref={videoRef}></div>
}
